"use client";

/**
 * ETF 重叠分析引导弹窗的 Promise opener（EDG-561 SC-04）。
 *
 * 逐行照 `openFinancialConnectPromptSheet`：模块级单实例 `pendingSheet`、`callEdgenModalWhenRootReadyAsync`、
 * `finish` 带 `settled`。结果：
 * - Connect / Upload → `"connect"` / `"upload"`；
 * - scrim / 下滑 / Esc → `null`（回发起页原位，不算任何主动动作），并上报 `overlap_guide_close{method}`。
 *
 * Web 没有等价于 KMP Surface Manager 的调度器，沿用 `open*()` + pending 守卫（C-10）。
 */

import type { MutableRefObject } from "react";
import { EdgenModalCreateAble } from "@components/EdgenModal";
import { callEdgenModalWhenRootReadyAsync } from "@components/EdgenModal/waitForEdgenModalRoot";
import { Track } from "@/src/lib/tracking";
import { overlapAttributionParams } from "../tracking/overlapAttribution";
import type { OverlapEntryContext } from "../types";
import {
  OverlapGuideContent,
  type OverlapGuideAction,
} from "./OverlapGuide/OverlapGuideContent";
import type { GuideCloseMethod } from "./OverlapGuide/useGuideCloseMethod";

export type { OverlapGuideAction } from "./OverlapGuide/OverlapGuideContent";

let pendingSheet: Promise<OverlapGuideAction | null> | null = null;

/** H5 底部抽屉 16 圆角（上）/ PC 居中 480、12 圆角；PC 动效为局部 slide-up（globals.css） */
const GUIDE_SHELL_CLASS =
  "[--modal-radius:16px_16px_0_0] md:[--modal-radius:12px] [--modal-width:100%] md:[--modal-width:480px]";

export const openEtfOverlapGuideSheet = (
  ctx: OverlapEntryContext,
): Promise<OverlapGuideAction | null> => {
  if (pendingSheet) {
    return pendingSheet;
  }
  // 关闭后焦点回入口元素
  const opener =
    typeof document !== "undefined"
      ? (document.activeElement as HTMLElement | null)
      : null;
  const closeMethodRef: MutableRefObject<() => GuideCloseMethod> = {
    current: () => "scrim",
  };

  pendingSheet = callEdgenModalWhenRootReadyAsync(
    () =>
      new Promise<OverlapGuideAction | null>((resolve) => {
        let settled = false;
        let modalInstance: Promise<boolean> | undefined;

        const finish = (result: OverlapGuideAction | null) => {
          if (settled) {
            return;
          }
          settled = true;
          pendingSheet = null;
          if (!modalInstance) {
            EdgenModalCreateAble.end(false);
          } else {
            EdgenModalCreateAble.end(modalInstance, false);
          }
          resolve(result);
          opener?.focus?.();
        };

        modalInstance = EdgenModalCreateAble.call({
          children: (
            <OverlapGuideContent
              entrySource={ctx.entry_source}
              closeMethodRef={closeMethodRef}
              onAction={(action) => finish(action)}
            />
          ),
          className: GUIDE_SHELL_CLASS,
          contentStyle: {
            borderRadius: "var(--modal-radius)",
            width: "var(--modal-width)",
            maxHeight: "90vh",
            overflow: "hidden",
            padding: 0,
          },
          containerStyle: { maxHeight: "none", overflowY: "visible" },
          transitionName: "etf-overlap-guide-modal",
          showClose: false,
          maskClosable: true,
          swipeToClose: true,
          destroyOnClose: true,
          onDismiss: () => {
            if (settled) {
              return;
            }
            Track.event("overlap_guide_close", {
              ...overlapAttributionParams(),
              entry_source: ctx.entry_source,
              method: closeMethodRef.current(),
            });
            finish(null);
          },
        });
      }),
  ).catch((error) => {
    pendingSheet = null;
    throw error;
  });

  return pendingSheet;
};
